import React, { useEffect, useState } from 'react';
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";

am4core.useTheme(am4themes_animated);

export default function BarChart({ dataSet = [], chartOnClick, chartWidth }) {
    const [themeSignature, setThemeSignature] = useState("");

    useEffect(() => {
        const getThemeSignature = () => {
            const rootStyles = getComputedStyle(document.documentElement);
            const textColor = rootStyles.getPropertyValue("--text-color").trim() || "#000000";
            const gridColor = rootStyles.getPropertyValue("--input-border-color").trim() || "#444444";
            return `${textColor}|${gridColor}`;
        };

        setThemeSignature(getThemeSignature());

        const observer = new MutationObserver(() => {
            const nextSignature = getThemeSignature();
            setThemeSignature((prev) => (prev === nextSignature ? prev : nextSignature));
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["style"],
        });

        return () => {
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        const rootStyles = getComputedStyle(document.documentElement);
        const textColor = rootStyles.getPropertyValue("--text-color").trim() || "#000000";
        const gridColor = rootStyles.getPropertyValue("--input-border-color").trim() || "#444444";
        if (!Array.isArray(dataSet) || dataSet.length === 0) {
            return;
        }

        // Create chart instance
        let chart = am4core.create("barchartdiv", am4charts.XYChart);
        chart.data = dataSet;

        // Set padding and disable logo
        chart.padding(40, 40, 40, 40);
        chart.logo.disabled = true;

        // Create category axis (dates)
        let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
        categoryAxis.dataFields.category = "_id";
        categoryAxis.renderer.grid.template.location = 0;
        categoryAxis.renderer.minGridDistance = 60;
        categoryAxis.renderer.labels.template.tooltipText = "{_id}";
        categoryAxis.renderer.labels.template.wrap = true;
        categoryAxis.renderer.labels.template.fill = am4core.color(textColor);
        categoryAxis.renderer.grid.template.stroke = am4core.color(gridColor);
        categoryAxis.renderer.line.stroke = am4core.color(gridColor);
        categoryAxis.fontSize = 12;
        categoryAxis.renderer.labels.template.rotation = -45;
        categoryAxis.renderer.labels.template.adapter.add("dy", (dy, target) => {
            return dy + 15; // spacing for rotated labels
        });

        // Create value axis (counts)
        let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        // valueAxis.strictMinMax = true; // Prevent automatic range extension
        valueAxis.min = 0; // Start at 0 (natural number)
        valueAxis.maxPrecision = 0; // No decimal places
        valueAxis.fontSize = 12;
        valueAxis.tooltip.disabled = true;
        valueAxis.renderer.labels.template.fill = am4core.color(textColor);
        valueAxis.renderer.grid.template.stroke = am4core.color(gridColor);
        valueAxis.renderer.line.stroke = am4core.color(gridColor);

        // Force whole numbers on labels
        valueAxis.renderer.labels.template.adapter.add("text", (text) => {
            return parseInt(text, 10); // Convert to integer
        });

        // Define series configurations
        const seriesConfig = [
            { field: "totalGateIn", name: "Gate In", color: "#66db97ff" },
            { field: "totalWeighbridgeIn", name: "WB In", color: "#bdc3c7" },
            { field: "totalWeighbridgeOut", name: "WB Out", color: "#f1c40f" },
            { field: "totalGateOut", name: "Gate Out", color: "#ff7700" }
        ];

        // Pre-calculate totals
        const totals = {};
        seriesConfig.forEach(({ field, name }) => {
            totals[field] = dataSet.reduce((sum, item) => sum + (item[field] || 0), 0);
        });
        // Create series dynamically
        seriesConfig.forEach(({ field, name, color }) => {
            if (dataSet.some(item => item[field] !== undefined)) {
                let series = chart.series.push(new am4charts.ColumnSeries());
                series.dataFields.valueY = field;
                series.dataFields.categoryX = "_id";
                series.name = `${name} (${totals[field]})`;
                series.tooltipText = `${name}: [bold]{valueY}[/]`;
                series.columns.template.fill = am4core.color(color);
                series.columns.template.stroke = am4core.color(color);
                series.columns.template.column.cornerRadiusTopLeft = 5;
                series.columns.template.column.cornerRadiusTopRight = 5;
            } else {
                console.warn(`Field '${field}' not found in data. Skipping series.`);
            }
        });

        // legend
        chart.legend = new am4charts.Legend();
        chart.legend.position = "top";
        chart.legend.labels.template.fill = am4core.color(textColor);
        chart.legend.valueLabels.template.fill = am4core.color(textColor);

        // cursor and scrollbar (if needed)
        chart.cursor = new am4charts.XYCursor();
        if (dataSet.length > 3) {
            chart.scrollbarX = new am4core.Scrollbar();
            chart.scrollbarX.parent = chart.bottomAxesContainer;
        }
        chart.series.each(series => {
            series.columns.template.width = am4core.percent(60);
            series.columns.template.maxWidth = 60;
        });
        // Cleanup
        return () => chart.dispose();
    }, [dataSet, chartOnClick, themeSignature]);

    return (
        <div id="barchartdiv" style={{ width: chartWidth || "100%", height: "500px" , marginTop:"-2rem"}}></div>
    );
};